import React, { Fragment, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Typography, TextField, Card, CardHeader, Checkbox } from "@mui/material";
import Close from "@mui/icons-material/Close";
import isNil from "lodash/isNil";
import isEqual from "lodash/isEqual";
import isArray from "lodash/isArray";
import { timeConversion, restClient } from "client-app-core";
import { useSelector, useDispatch } from "react-redux";
import { getEntityIconsByType } from "orion-components/GlobalData/Selectors";

import { Translate, getTranslation } from "orion-components/i18n";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import {
	AlertReference,
	TargetingIcon,
	formatAlertReference,
	isAlertReferencePrefix
} from "orion-components/SharedComponents";
import useAlertReferenceSearch from "orion-components/SharedComponents/AlertReference/useAlertReferenceSearch";
import { matchesAlertReferenceSearch } from "orion-components/SharedComponents/NotificationSearchUtils";
import { DatePicker } from "orion-components/CBComponents";
import EventBus from "orion-components/SharedComponents/EventBus";
import * as actionCreators from "orion-components/Profiles/AlertProfile/actions";
import { processAggregates } from "../../../SharedComponents/trans-utils";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";
import { AlertPanelDetailWidget } from "orion-components/Profiles/Widgets";
import { addContext, removeContext } from "orion-components/ContextualData/Actions";

const propTypes = {
	closeArchive: PropTypes.func,
	timeFormat: PropTypes.string,
	reopenNotification: PropTypes.func.isRequired
};

const defaultProps = {
	closeArchive: null,
	timeFormat: "12-hour"
};
const datePickerLabelStyle = { color: "#838283", fontWeight: 500 };

const matchesArchivedSearch = (notification, searchString) => {
	if (!searchString) return true;

	const normalizedQuery = searchString.toLowerCase();
	return (
		matchesAlertReferenceSearch(searchString, notification.alertReference) ||
		[notification.message, notification.alertReference, formatAlertReference(notification.alertReference)]
			.filter(Boolean)
			.some(searchValue => searchValue.toLowerCase().includes(normalizedQuery))
	);
};

const intersectAlertsById = (alerts, allowedAlerts) => {
	const allowedIds = new Set(allowedAlerts.map(alert => alert.id));
	return alerts.filter(alert => allowedIds.has(alert.id));
};

const usePrevious = value => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

const ArchivedNotifications = props => {
	const { closeArchive, archive, fetching, shouldFetch, fetchNextPage, dir } = props;
	const dispatch = useDispatch();
	const [filterByDate, setFilterByDate] = useState(null);
	const timeFormat = useSelector(state => state.appState.global?.timeFormat);
	const locale = useSelector(state => state.i18n.locale);
	const [notifications, setNotifications] = useState([]);
	const [checkedIds, setCheckedIds] = useState(new Set());
	const [isDateFilterApplied, setIsDateFilterApplied] = useState(false);
	const [selectedArchivedAlert, setSelectedArchivedAlert] = useState(null);
	const [searchString, setSearchString] = useState("");
	const {
		isReferenceSearch,
		results: referenceSearchResults
	} = useAlertReferenceSearch({
		value: searchString,
		closed: true
	});

	const { reopenAlert, dumpArchive } = actionCreators;
	const eventLookups = useSelector(state => state.eventLookups);
	const colors = useSelector(state => getEntityIconsByType(state)("frame_colors"));
	const { priorities, types } = eventLookups;
	const isFetchingRef = useRef(false);
	const filterByDateRef = useRef(null);
	const dateFilteredNotificationsRef = useRef([]);
	const scrollBarsEnabled = useSelector(state => getScrollBarAppState(state));

	const prevNotifications = usePrevious(props.notifications);

	useEffect(() => {
		if (!isReferenceSearch && !isEqual(prevNotifications, props.notifications)) {
			const filteredNotifications = props.notifications.filter(notification =>
				matchesArchivedSearch(notification, searchString)
			);
			const notificationsWithAggregates = processAggregates(filteredNotifications);
			setNotifications(notificationsWithAggregates);
		}
	}, [props.notifications, isReferenceSearch, searchString]);

	useEffect(() => {
		if (isReferenceSearch && Array.isArray(referenceSearchResults)) {
			const filteredResults = isDateFilterApplied
				? intersectAlertsById(referenceSearchResults, dateFilteredNotificationsRef.current)
				: referenceSearchResults;
			setNotifications(processAggregates(filteredResults));
		}
	}, [isReferenceSearch, referenceSearchResults, isDateFilterApplied]);

	const searchNotification = e => {
		const value = e.target.value;
		setSearchString(value);
		if (isAlertReferencePrefix(value)) {
			return;
		}

		const selectNotification = isDateFilterApplied ? dateFilteredNotificationsRef.current : props.notifications;
		const filterNotification = selectNotification.filter(notification =>
			matchesArchivedSearch(notification, value)
		);
		const notificationsWithAggregates = processAggregates(filterNotification);
		setNotifications(notificationsWithAggregates);
	};

	const handleDateFilter = () => event => {
		const value = event && event.target && !isNil(event.target.value) ? event.target.value : event;
		setFilterByDate(value);
		filterByDateRef.current = value;
		if (value) {
			const startDate = new Date(value).setHours(0, 0, 0, 0);
			const endDate = new Date(value).setHours(23, 59, 59, 999);

			restClient.exec_get(
				`/ecosystem/api/alerts?isClosed=${true}&startDate=${startDate}&endDate=${endDate}`,
				(err, response) => {
					if (err) console.log(err);
					if (response && isArray(response)) {
						const notificationsWithAggregates = processAggregates(response);
						const filteredNotifications =
							isReferenceSearch && Array.isArray(referenceSearchResults)
								? intersectAlertsById(referenceSearchResults, response)
								: response.filter(notification =>
										matchesArchivedSearch(notification, searchString)
									);
						setNotifications(processAggregates(filteredNotifications));
						setIsDateFilterApplied(true);
						dateFilteredNotificationsRef.current = notificationsWithAggregates;
					}
				}
			);
		} else {
			const filteredNotifications =
				isReferenceSearch && Array.isArray(referenceSearchResults)
					? referenceSearchResults
					: props.notifications.filter(notification =>
							matchesArchivedSearch(notification, searchString)
						);
			setNotifications(processAggregates(filteredNotifications));
			setIsDateFilterApplied(false);
			dateFilteredNotificationsRef.current = [];
		}
	};

	const handleCheckbox = (id, checked) => {
		setCheckedIds(prev => {
			const next = new Set(prev);
			if (checked) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	};

	const handleReactivate = () => {
		const ReactivateItems = notifications.filter(notification => checkedIds.has(notification.id));
		ReactivateItems.map(notification => {
			EventBus.publish("reopenAlerts", notification.id);
			dispatch(reopenAlert(notification));
		});
		dispatch(dumpArchive());
		closeArchive();
	};

	const handleCloseArchive = () => {
		if (selectedArchivedAlert?.id) {
			dispatch(removeContext(selectedArchivedAlert.id));
		}
		dispatch(dumpArchive());
		closeArchive();
	};

	useEffect(() => {
		if (archive) {
			handleScroll();
		}
	}, []);

	useEffect(() => {
		isFetchingRef.current = fetching;
	}, [fetching]);

	const scrollEventHandler = event => {
		const element = event.target;
		const { scrollTop, clientHeight, scrollHeight } = element;
		if (
			Math.floor(scrollHeight - scrollTop) <= clientHeight && // --> If we scroll to bottom
			!isFetchingRef.current && // --> AND not already fetching
			shouldFetch &&
			!filterByDateRef.current
		) {
			// --> AND we haven't fetched the same data in the past X seconds
			fetchNextPage();
		}
	};

	const handleScroll = () => {
		const element = document.getElementById("virtualized-archived-notification-list");
		if (element) {
			element.addEventListener("scroll", scrollEventHandler);
		}
	};

	const getAlertSettingByProperty = (alert, setting, extract) => {
		const priority = priorities?.find(item => item.id === alert?.eventPriorityId);
		const type = types?.find(item => item.id === alert?.eventTypeId);

		switch (setting) {
			case "priority":
				return priority ? priority[extract] : priority;
			case "type":
				return type ? type[extract] : type;
			default:
				return;
		}
	};

	const handleViewArchivedAlert = (event, alert) => {
		event.stopPropagation();
		dispatch(addContext(alert.id, alert));
		setSelectedArchivedAlert(alert);
	};

	const closeArchivedAlertProfile = () => {
		if (selectedArchivedAlert?.id) {
			dispatch(removeContext(selectedArchivedAlert.id));
		}
		setSelectedArchivedAlert(null);
	};

	return (
		<div
			id="virtualized-archived-notification-list"
			style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}
			data-testid="archived-notifications-archive-drawer"
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					backgroundColor: "#1D1D1D",
					paddingTop: 13,
					paddingBottom: 7,
					height: 44
				}}
			>
				<div
					data-testid="archived-notifications-back-button"
					style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
					onClick={() => (selectedArchivedAlert?.id ? closeArchivedAlertProfile() : handleCloseArchive())}
				>
					<i className="material-icons" style={{ marginLeft: 10, color: "#FFF" }}>
						keyboard_arrow_left
					</i>
					<div style={{ color: "#FFF", fontSize: 15, marginLeft: 9 }}>
						<Translate value="global.dock.notifications.main.alarmArchive.back" />
					</div>
				</div>
				<div style={{ color: "#fff" }}>
					{!selectedArchivedAlert?.id && (
						<Translate value="global.dock.notifications.main.alarmArchive.title" />
					)}
				</div>
				<div>
					<Close
						data-testid="archived-notifications-close-button"
						style={{ cursor: "pointer", fontSize: 18, marginRight: 10 }}
						sx={{ color: "#FFF" }}
						onClick={() => (selectedArchivedAlert?.id ? closeArchivedAlertProfile() : handleCloseArchive())}
					/>
				</div>
			</div>
			{selectedArchivedAlert?.id && (
				<div
					style={{
						backgroundColor: "#E8585D",
						color: "#FFFFFF",
						fontSize: 14,
						fontWeight: 600,
						textAlign: "center",
						padding: "8px 0"
					}}
					data-testid="archived-notifications-read-only-banner"
				>
					<Translate value="global.dock.notifications.main.alarmArchive.readOnlyBanner" />
				</div>
			)}
			<div
				style={{
					padding: selectedArchivedAlert?.id ? 0 : "12px 0 12px 13px",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					flex: 1,
					minHeight: 0
				}}
			>
				{selectedArchivedAlert?.id ? (
					<AlertPanelDetailWidget
						key={selectedArchivedAlert.id}
						readOnly={true}
						notificationId={selectedArchivedAlert.id}
						notification={selectedArchivedAlert}
						activityId={selectedArchivedAlert.activityId}
						closeDetailView={closeArchivedAlertProfile}
						dir={dir}
						isExpanded={true}
					/>
				) : (
					<>
						<div className="search-field" style={{ marginRight: 22 }} data-testid="orion-components-dock-notifications-components-archived-notifications-div">
							<i className="material-icons" style={{ margin: "10px 10px 0px 0px" }}>
								search
							</i>
							<TextField
								id="search-field"
								data-testid="archived-notifications-search-input"
								placeholder={getTranslation("global.dock.notifications.main.alarmArchive.search")}
								onChange={searchNotification}
								variant="standard"
								style={{
									backgroundColor: "transparent",
									flexGrow: 1
								}}
							/>
						</div>
						<div style={{ display: "flex", alignItems: "center", marginRight: 22 }}>
							<i
								className="material-icons"
								style={{ margin: "10px 10px 0px 0px", fontSize: 25, color: "#838283" }}
							>
								event
							</i>
							<div style={{ flexGrow: 1 }} data-testid="archived-notifications-date-filter">
								<DatePicker
									id="filter-date"
									key="filter-date"
									label={getTranslation("global.dock.notifications.main.alarmArchive.filterByDate")}
									value={filterByDate}
									onSubmit={handleDateFilter()}
									labelStyle={datePickerLabelStyle}
									dir={dir} data-testid="orion-components-dock-notifications-components-archived-notifications-date-picker"
								/>
							</div>
						</div>
						<Button
							data-testid="archived-notifications-reactivate-button"
							style={{
								height: 30,
								color: "#fff",
								padding: 6,
								fontSize: 12,
								margin: "24px 22px 16px 0"
							}}
							sx={{
								"&:hover": {
									backgroundColor: "#85C7EF !important"
								}
							}}
							onClick={() => {
								handleReactivate();
							}}
							variant="contained"
							color="primary"
						>
							<Translate value="global.dock.notifications.main.alarmArchive.reactivate" />
						</Button>
						<div
							style={{
								overflow: "scroll",
								flexGrow: 1,
								...(!scrollBarsEnabled ? { marginRight: 13 } : {})
							}}
						>
							{notifications &&
								notifications?.map((alert, index) => (
									<>
										<div
											style={{
												width: "100%",
												display: "flex",
												alignItems: "center",
												marginLeft: 5
											}}
											data-testid="archived-notifications-item"
										>
											<Card style={{ marginBottom: 8, borderRadius: 5, width: "100%" }}>
												<CardHeader
													sx={{
														color: "#fff",
														backgroundColor: checkedIds.has(alert.id)
															? "#494D53"
															: "#2c2b2d",
														border: checkedIds.has(alert.id) ? "none" : "1px solid #494D53",
														padding: "8px",
														"& .MuiCardHeader-action": {
															alignSelf: "center",
															margin: 0
														}
													}}
													avatar={
														<div
															style={{
																display: "flex",
																alignItems: "center"
															}}
														>
															{alert?.geometry ? (
																<TargetingIcon
																	geometry={alert.geometry}
																	defaultVisibility={true}
																/>
															) : null}
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	padding: 10,
																	borderRadius: 5,
																	backgroundColor:
																		colors?.[
																			getAlertSettingByProperty(
																				alert,
																				"type",
																				"color"
																			)
																		]?.color ?? "#979798",
																	height: 26,
																	width: 26,
																	marginLeft: 8
																}}
															>
																<SpriteIcon
																	iconName={
																		getAlertSettingByProperty(
																			alert,
																			"type",
																			"icon"
																		) ?? "icon_information"
																	}
																	customHeight="17.5px"
																	customWidth="17.5px"
																/>
															</div>
														</div>
													}
													title={alert?.message}
													titleTypographyProps={{
														style: {
															lineHeight: "1.25em",
															fontSize: "13px"
														}
													}}
													subheader={
														<Fragment>
															<AlertReference value={alert.alertReference} />
															<div style={{ display: "flex" }}>
																<Typography
																	style={{ fontSize: "11px" }}
																	color="#82878C"
																>
																	{getAlertSettingByProperty(alert, "type", "name")}
																</Typography>
																{getAlertSettingByProperty(alert, "type", "name") ? (
																	<Typography
																		style={{ fontSize: "11px", margin: "0px 5px" }}
																		color="#82878C"
																	>
																		|
																	</Typography>
																) : null}
																<Typography
																	style={{ fontSize: "11px" }}
																	color="#82878C"
																>
																	{timeConversion.convertToUserTime(
																		alert?.activityDate || alert?.createdDate,
																		`full_${timeFormat}`,
																		locale
																	)}
																</Typography>
																{(alert.occurrenceCount || 1) > 1 && (
																	<>
																		<Typography
																			style={{
																				fontSize: "11px",
																				margin: "0px 5px"
																			}}
																			color="#82878C"
																		>
																			|
																		</Typography>
																		<Typography
																			style={{ fontSize: "11px" }}
																			color="#82878C"
																		>
																			<Translate
																				value="global.dock.notifications.main.alarmArchive.aggregateCount"
																				count={alert.occurrenceCount}
																			/>
																		</Typography>
																	</>
																)}
															</div>
															{alert?.occurrenceCount === alert?.ackedCount ? (
																<Typography
																	style={{ fontSize: "12px" }}
																	color="#82878C"
																>
																	{alert?.occurrences?.[0]?.acknowledgedByUserName ||
																		alert?.occurrences?.[0]?.acknowledgedBy}
																</Typography>
															) : null}
														</Fragment>
													}
													subheaderTypographyProps={{
														component: "div",
														styles: {
															color: "#fff"
														}
													}}
													action={
														<Button
															variant="outlined"
															size="small"
															sx={{
																color: "#fff",
																borderColor: "#82878C",
																fontSize: 11,
																minWidth: 48,
																height: 24,
																padding: "0 8px",
																marginTop: "12px",
																textTransform: "none",
																"&:hover": {
																	borderColor: "#85C7EF",
																	backgroundColor: "transparent"
																}
															}}
															onClick={e => handleViewArchivedAlert(e, alert)} data-testid="orion-components-dock-notifications-components-archived-notifications-button"
														>
															<Translate value="global.dock.notifications.main.alarmArchive.view" />
														</Button>
													}
												/>
											</Card>
											<Checkbox
												checked={checkedIds.has(alert.id)}
												data-testid="archived-notifications-item-checkbox"
												sx={{
													color: "#494D53",
													"&.Mui-checked": {
														color: "primary"
													}
												}}
												onChange={e => handleCheckbox(alert.id, e.target.checked)}
											/>
										</div>
									</>
								))}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

ArchivedNotifications.propTypes = propTypes;
ArchivedNotifications.defaultProps = defaultProps;

export default ArchivedNotifications;
